import { Button } from "@/components/ui/button";
import { SearchDropdown } from "@/components/SearchDropdown";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { retailer } from "@/data/content";
import { useUserLocation } from "@/Locations/hooks/use-user-location";
import { StoreSelectorSheet } from "@/Locations/components/StoreSelectorSheet";
import { AppDownloadModal } from "@/SiteInfo/components/AppDownloadModal";
import { useLocations } from "@/Locations/hooks/use-locations";
import { useSiteInfo } from "@/SiteInfo/hooks/use-site-info";
import { useEvents } from "@/Events/hooks/use-events";
import { EventsSheet } from "@/Events/components/EventsSheet";
import { NotificationsModal } from "@/Notifications/components/NotificationsModal";
import { NotificationBanner } from "@/Notifications/components/NotificationBanner";
import { CouponAuthModal } from "@/Coupons/components/CouponAuthModal";
import { AccountSheet } from "@/Coupons/components/AccountSheet";
import { useCouponAuth } from "@/Coupons/hooks/use-coupon-auth";
import { useCustomerProfile } from "@/Coupons/hooks/use-customer-profile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatLocationHours } from "@/Locations/lib/format-hours";
import { Badge } from "@/components/ui/badge";
import { Location } from "@/Locations/types/locations";
import { IconBrandApple, IconBrandGooglePlay, IconClock, IconExternalLink, IconMapPin, IconMenu2, IconPhone, IconTag, IconUserCircle, IconX } from '@tabler/icons-react';

type Platform = 'ios' | 'android' | 'macos' | 'desktop';

function detectPlatform(): Platform {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }

  if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.platform)) {
    return 'macos';
  }

  if (/android/i.test(userAgent)) {
    return 'android';
  }

  return 'desktop';
}

const LocationDetailsDialog = ({ location, open, onOpenChange }: { location: Location | null; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { setLocation, isSelected, clearLocation } = useUserLocation();
  
  if (!location) return null;
  
  const isLocationSelected = isSelected(location.LocationTitle);
  
  const handleSelectLocation = () => {
    setLocation(location.LocationTitle);
    onOpenChange(false);
  };
  
  const handleClearLocation = () => {
    clearLocation();
  };

  const formattedHours = formatLocationHours(location.LocationHours);
  const fullAddress = [
    location.LocationAddress.LocationAddressLineOne,
    location.LocationAddress.LocationAddressLineTwo,
    `${location.LocationAddress.LocationAddressCity}, ${location.LocationAddress.LocationAddressState} ${location.LocationAddress.LocationAddressZipCode}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{location.LocationTitle}</DialogTitle>
          {location.LocationBanner && (
            <DialogDescription className="text-base">{location.LocationBanner}</DialogDescription>
          )}
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <IconMapPin className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Address</p>
                <p className="text-sm text-muted-foreground">{fullAddress}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <IconPhone className="h-5 w-5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Phone</p>
                <p className="text-sm text-muted-foreground">{location.LocationPhoneNumber}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <IconClock className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Hours</p>
                <p className="text-sm text-muted-foreground">{formattedHours}</p>
              </div>
            </div>
          </div>

          {location.LocationCoupons && (
            <div>
              <Badge variant="secondary" className="mb-2">
                <IconTag className="h-3 w-3 mr-1" />
                Coupons Available
              </Badge>
            </div>
          )}

          {location.LocationServices && location.LocationServices.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Services</p>
              <div className="flex flex-wrap gap-2">
                {location.LocationServices.map((service, idx) => (
                  <Badge key={idx} variant="outline">
                    {service.LocationServiceDepartment}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {location.LocationLinks && location.LocationLinks.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Links</p>
              <div className="flex flex-wrap gap-2">
                {location.LocationLinks.map((link, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a
                      href={link.LocationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconExternalLink className="h-4 w-4 mr-2" />
                      Visit Site
                    </a>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {isLocationSelected ? (
            <Button
              variant="destructive"
              onClick={handleClearLocation}
              className="w-full sm:w-auto"
            >
              Clear Selection
            </Button>
          ) : (
            <Button
              onClick={handleSelectLocation}
              className="w-full sm:w-auto"
            >
              Set as my store
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isStoreSelectorOpen, setIsStoreSelectorOpen] = useState(false);
  const [initialSelectorView, setInitialSelectorView] = useState<'stores' | 'details'>('stores');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'register'>('signin');
  const [accountSheetOpen, setAccountSheetOpen] = useState(false);
  const [appStoreModalOpen, setAppStoreModalOpen] = useState(false);
  const [googlePlayModalOpen, setGooglePlayModalOpen] = useState(false);
  const { selectedLocation } = useUserLocation();
  const { data: locations } = useLocations();
  const { data: siteInfo } = useSiteInfo();
  const { data: events } = useEvents();
  const { isAuthenticated } = useCouponAuth();
  const { profile } = useCustomerProfile();

  // Get first name from Loyalty Lane Shopper profile (LL returns ALL CAPS)
  const customerFirstName = profile?.FirstName
    ? profile.FirstName.toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase())
    : '';

  const hasEvents = events && events.length > 0;
  const hasCoupons = siteInfo?.SiteHasCoupons ?? false;
  const hasMobileApp = siteInfo?.SiteHasMobileApp ?? false;
  const hasAboutUs = import.meta.env.VITE_FEATURE_ABOUT_US === 'true';
  const hasCareers = import.meta.env.VITE_FEATURE_CAREERS === 'true';
  const careersUrl = import.meta.env.VITE_CAREERS_URL;
  const hasOnlineShopping = import.meta.env.VITE_FEATURE_ONLINE_SHOPPING === 'true';
  const onlineShoppingUrl = import.meta.env.VITE_ONLINE_SHOPPING_URL;
  const appStoreUrl = siteInfo?.AppStoreURL;
  const googlePlayUrl = siteInfo?.GooglePlayStoreURL;
  const siteName = siteInfo?.SiteTitle ?? "Store";

  const handleAppStoreClick = () => {
    if (!appStoreUrl) return;
    const platform = detectPlatform();
    if (platform === 'ios') {
      window.open(appStoreUrl, '_blank');
    } else {
      setAppStoreModalOpen(true);
    }
  };

  const handleGooglePlayClick = () => {
    if (!googlePlayUrl) return;
    const platform = detectPlatform();
    if (platform === 'android') {
      window.open(googlePlayUrl, '_blank');
    } else {
      setGooglePlayModalOpen(true);
    }
  };

  // Find the selected location object
  const selectedLocationData = useMemo(() => {
    if (!selectedLocation || !locations) return null;
    return locations.find((loc) => loc.LocationTitle === selectedLocation) || null;
  }, [selectedLocation, locations]);

  const handleLocationClick = () => {
    if (selectedLocation && selectedLocationData) {
      setInitialSelectorView('details');
      setIsStoreSelectorOpen(true);
    } else {
      setInitialSelectorView('stores');
      setIsStoreSelectorOpen(true);
    }
  };

  return (
    <>
      <NotificationBanner />
      <header className="sticky top-0 z-50 bg-background border-b border-border shadow-sm">
      {/* Top bar with store info */}
      <div className="bg-primary text-primary-foreground py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs sm:text-sm">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <IconMapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              <button
                onClick={handleLocationClick}
                className="hover:underline cursor-pointer"
              >
{selectedLocationData?.LocationTitle || "Find Your Store"}
              </button>
            </div>
            {hasEvents && (
              <>
                <div className="h-3 sm:h-4 w-px bg-white/30"></div>
                <button
                  onClick={() => setIsEventsModalOpen(true)}
                  className="hover:underline"
                >
                  Events
                </button>
              </>
            )}
            {hasAboutUs && (
              <>
                <div className="h-3 sm:h-4 w-px bg-white/30"></div>
                <Link
                  to="/about"
                  className="hover:underline"
                >
                  About Us
                </Link>
              </>
            )}
            {hasCareers && careersUrl && (
              <>
                <div className="h-3 sm:h-4 w-px bg-white/30"></div>
                <a
                  href={careersUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  Careers
                </a>
              </>
            )}
            {hasMobileApp && (
              <>
                <div className="h-3 sm:h-4 w-px bg-white/30 hidden sm:block"></div>
                <div className="hidden sm:flex items-center gap-3">
                  {appStoreUrl && (
                    <button
                      onClick={handleAppStoreClick}
                      className="hover:opacity-75 transition-opacity"
                      aria-label="Download on App Store"
                    >
                      <IconBrandApple className="h-5 w-5" />
                    </button>
                  )}
                  {googlePlayUrl && (
                    <button
                      onClick={handleGooglePlayClick}
                      className="hover:opacity-75 transition-opacity"
                      aria-label="Get it on Google Play"
                    >
                      <IconBrandGooglePlay className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {hasCoupons && isAuthenticated && (
              <>
                {customerFirstName && (
                  <span className="hidden sm:inline-flex items-center gap-1.5">
                    <IconUserCircle className="h-4 w-4 shrink-0" />
                    Hello, {customerFirstName}
                  </span>
                )}
                <div className="hidden sm:block h-3 sm:h-4 w-px bg-white/30"></div>
                <button
                  onClick={() => setAccountSheetOpen(true)}
                  className="hover:underline"
                >
                  My Account
                </button>
              </>
            )}
            {hasCoupons && !isAuthenticated && (
              <>
                <button
                  onClick={() => { setAuthMode('register'); setAuthModalOpen(true); }}
                  className="hover:underline"
                >
                  Register
                </button>
                <div className="h-3 sm:h-4 w-px bg-white/30"></div>
                <button
                  onClick={() => { setAuthMode('signin'); setAuthModalOpen(true); }}
                  className="hover:underline"
                >
                  Sign In
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <IconX className="h-6 w-6" /> : <IconMenu2 className="h-6 w-6" />}
            </Button>
            <Link to="/">
              {siteInfo?.SiteLogo?.url ? (
                <img
                  src={siteInfo.SiteLogo.url}
                  alt={siteInfo.SiteTitle}
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <h1 className="text-2xl font-bold text-primary hover:text-primary/90 transition-colors">
                  {siteInfo?.SiteTitle || retailer.name}
                </h1>
              )}
            </Link>
          </div>

          {/* Navigation - Desktop */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link to="/" className="text-foreground hover:text-primary font-medium transition-colors">Home</Link>
            <Link to="/ads" className="text-foreground hover:text-primary font-medium transition-colors">Ads</Link>
            <Link to="/locations" className="text-foreground hover:text-primary font-medium transition-colors">Locations</Link>
            {hasCoupons && (
              <Link to="/coupons" className="text-foreground hover:text-primary font-medium transition-colors">Coupons</Link>
            )}
            {hasOnlineShopping && onlineShoppingUrl && (
              <a
                href={onlineShoppingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:text-primary font-medium transition-colors"
              >
                Shop Now
              </a>
            )}
            <Link to="/recipes" className="text-foreground hover:text-primary font-medium transition-colors">Recipes</Link>
            <Link to="/contact" className="text-foreground hover:text-primary font-medium transition-colors">Contact</Link>
          </nav>

          {/* Search bar */}
          <div className="flex-1 max-w-md hidden md:block">
            <SearchDropdown placeholder="Search our website..." />
          </div>

          {/* CTA Button */}
          <Button
            variant="default"
            className="hidden sm:inline-flex"
            onClick={() => {
              setInitialSelectorView('stores');
              setIsStoreSelectorOpen(true);
            }}
          >
            {selectedLocation ? "Change Store" : "Find Your Store"}
          </Button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-border pt-4 flex flex-col gap-2">
            <Link to="/" className="text-lg text-foreground hover:text-primary font-medium py-2" onClick={() => setIsMenuOpen(false)}>Home</Link>
            <Link to="/ads" className="text-lg text-foreground hover:text-primary font-medium py-2" onClick={() => setIsMenuOpen(false)}>Ads</Link>
            <Link to="/locations" className="text-lg text-foreground hover:text-primary font-medium py-2" onClick={() => setIsMenuOpen(false)}>Locations</Link>
            {hasCoupons && (
              <Link to="/coupons" className="text-lg text-foreground hover:text-primary font-medium py-2" onClick={() => setIsMenuOpen(false)}>Coupons</Link>
            )}
            {hasOnlineShopping && onlineShoppingUrl && (
              <a
                href={onlineShoppingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg text-foreground hover:text-primary font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Shop Now
              </a>
            )}
            <Link to="/recipes" className="text-lg text-foreground hover:text-primary font-medium py-2" onClick={() => setIsMenuOpen(false)}>Recipes</Link>
            <Link to="/contact" className="text-lg text-foreground hover:text-primary font-medium py-2" onClick={() => setIsMenuOpen(false)}>Contact</Link>

            {/* Divider */}
            <div className="border-t border-border my-2"></div>

            {/* Account section */}
            {hasCoupons && (
              isAuthenticated ? (
                <button
                  className="text-lg text-foreground hover:text-primary font-medium py-2 text-left flex items-center gap-2"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setAccountSheetOpen(true);
                  }}
                >
                  <IconUserCircle className="h-5 w-5" />
                  My Account
                </button>
              ) : (
                <button
                  className="text-lg text-foreground hover:text-primary font-medium py-2 text-left flex items-center gap-2"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setAuthMode('signin');
                    setAuthModalOpen(true);
                  }}
                >
                  <IconUserCircle className="h-5 w-5" />
                  Sign In / Register
                </button>
              )
            )}

            {/* App download links */}
            {hasMobileApp && (appStoreUrl || googlePlayUrl) && (
              <div className="flex items-center gap-4 py-2">
                {appStoreUrl && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleAppStoreClick();
                    }}
                    className="text-foreground hover:text-primary"
                    aria-label="Download on App Store"
                  >
                    <IconBrandApple className="h-6 w-6" />
                  </button>
                )}
                {googlePlayUrl && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleGooglePlayClick();
                    }}
                    className="text-foreground hover:text-primary"
                    aria-label="Get it on Google Play"
                  >
                    <IconBrandGooglePlay className="h-6 w-6" />
                  </button>
                )}
              </div>
            )}
          </nav>
        )}

        {/* Mobile search */}
        <div className="md:hidden mt-4">
          <SearchDropdown placeholder="Search our website..." />
        </div>
      </div>

      <StoreSelectorSheet
        open={isStoreSelectorOpen}
        onOpenChange={setIsStoreSelectorOpen}
        initialView={initialSelectorView}
      />

      <LocationDetailsDialog
        location={selectedLocationData}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
      />

      <EventsSheet
        open={isEventsModalOpen}
        onOpenChange={setIsEventsModalOpen}
      />

      <NotificationsModal
        open={isNotificationsModalOpen}
        onOpenChange={setIsNotificationsModalOpen}
      />

      <CouponAuthModal
        coupon={null}
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        initialMode={authMode}
      />

      <AccountSheet
        open={accountSheetOpen}
        onOpenChange={setAccountSheetOpen}
      />

      <AppDownloadModal
        open={appStoreModalOpen}
        onOpenChange={setAppStoreModalOpen}
        storeUrl={appStoreUrl}
        storeType="appstore"
        siteName={siteName}
      />

      <AppDownloadModal
        open={googlePlayModalOpen}
        onOpenChange={setGooglePlayModalOpen}
        storeUrl={googlePlayUrl}
        storeType="googleplay"
        siteName={siteName}
      />
    </header>
    </>
  );
};
